import React from "react";
import PropTypes from "prop-types";
import { Typography, Box, Alert, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { intl } from "i18n/provider"; // Assuming 'intl' is properly initialized and imported

const ConfirmationMessage = ({
  playerId,
  playerName,
  totalUC,
  cost,
  selectedCards = [],
  onRemoveCard,
}) => {
  const formattedMessage = intl.formatMessage(
    { id: "pubg_conformation_modal_msg" },
    {
      player_id: `<span style="font-weight: bold; font-size: 1.2em; ">${playerId}</span>`,
      player_name: `<span style="font-weight: bold; font-size: 1.2em;">${playerName}</span>`,
      total_uc: `<span style="font-weight: bold; font-size: 1.2em;">${totalUC}</span>`,
      cost: `<span style="font-weight: bold; font-size: 1.2em;">${cost}</span>`,
    }
  );

  const hasSelectedCards = Array.isArray(selectedCards) && selectedCards.length;

  return (
    <Alert
      severity="warning"
      sx={{
        marginTop: 2,
        marginBottom: 2,
      }}
    >
      <Typography
        variant="h5"
        dangerouslySetInnerHTML={{ __html: formattedMessage }}
      />
      {hasSelectedCards ? (
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: 2,
            padding: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {intl.formatMessage({ id: "pubg_confirmation_selected_cards" })}
          </Typography>
          {selectedCards.map((card) => {
            const ucMatch = card?.ean?.match(/\d+/);
            const baseUC = ucMatch ? parseInt(ucMatch[0], 10) : null;
            const ucLabel =
              baseUC !== null
                ? `${intl.formatNumber(baseUC)} UC`
                : card?.brand_name || card?.value || card?.ean || "-";

            const totalCardUC =
              baseUC !== null ? baseUC * Number(card?.quantity || 0) : null;

            const unitPriceValue = Number(card?.customer_price);
            const hasUnitPrice = Number.isFinite(unitPriceValue);
            const unitPriceDisplay = hasUnitPrice
              ? intl.formatNumber(unitPriceValue, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })
              : null;
            const totalPriceDisplay =
              hasUnitPrice && Number.isFinite(card?.quantity)
                ? intl.formatNumber(unitPriceValue * card.quantity, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                : null;

            return (
              <Box
                key={`${card?.id ?? card?.ean}-${card?.quantity}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                  padding: "12px 16px",
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  {card?.image_path ? (
                    <Box
                      component="img"
                      src={card.image_path}
                      alt={card?.brand_name || ucLabel}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1,
                        objectFit: "contain",
                        backgroundColor: "rgba(0,0,0,0.04)",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    />
                  ) : null}
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Typography fontWeight={600}>{ucLabel}</Typography>
                    {card?.brand_name ? (
                      <Typography variant="body2" color="text.secondary">
                        {card.brand_name}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    textAlign="right"
                    gap={0.5}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage(
                        { id: "pubg_confirmation_card_quantity" },
                        { quantity: card?.quantity || 0 }
                      )}
                    </Typography>
                    {totalCardUC !== null && (
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage(
                          { id: "pubg_confirmation_card_total_uc" },
                          {
                            totalUC: intl.formatNumber(totalCardUC, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }),
                          }
                        )}
                      </Typography>
                    )}
                    {unitPriceDisplay && totalPriceDisplay && (
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage(
                          { id: "pubg_confirmation_card_price_details" },
                          {
                            unitPrice: unitPriceDisplay,
                            quantity: card?.quantity || 0,
                            totalPrice: totalPriceDisplay,
                          }
                        )}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip
                    title={intl.formatMessage({
                      id: "pubg_confirmation_remove_card",
                    })}
                  >
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => onRemoveCard(card?.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Alert>
  );
};

ConfirmationMessage.propTypes = {
  playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  playerName: PropTypes.string,
  totalUC: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ean: PropTypes.string,
      brand_name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      customer_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      quantity: PropTypes.number,
    })
  ),
  onRemoveCard: PropTypes.func,
};

ConfirmationMessage.defaultProps = {
  playerId: "",
  playerName: "",
  totalUC: 0,
  cost: 0,
  selectedCards: [],
  onRemoveCard: () => {},
};

export default ConfirmationMessage;
